import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  SearchOutlined,
  ArrowForward,
  VerifiedOutlined,
  BoltOutlined,
  SupportAgentOutlined,
  ExtensionOutlined,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { loadListings } from "../api/publicApi";
import ListingCard from "../components/ListingCard";
import { tokens, radius, shadow } from "../../theme/theme";
import { LVIV_DISTRICTS, SITE } from "../siteConfig";
import { formatNumber, formatPriceShort, itemPriceInBase } from "../utils/format";
import { CHRISTMAS, FROST, HALLOWEEN, NIGHT, PIXEL_FONT, SPOOKY_FONT } from "../halloween/season";
import HalloweenHero, { NIGHT_SKY } from "../halloween/HalloweenHero";
import { Bat, Cobweb, Pumpkin } from "../halloween/sprites";
import ChristmasHero from "../christmas/ChristmasHero";
import Sprite from "../christmas/Sprite";
import { GarlandDrape } from "../christmas/Edges";
import { GARLAND, ICONS } from "../christmas/atlas";

/** Calls to action on the night theme are lit like a jack-o'-lantern. */
const PUMPKIN_BUTTON = {
  color: "#fff",
  background: "linear-gradient(180deg, #FF9A3D 0%, " + NIGHT.pumpkinDeep + " 100%)",
  boxShadow: "0 8px 22px rgba(228,87,11,0.45)",
  "&:hover": {
    background: "linear-gradient(180deg, #FFA852 0%, #D14E08 100%)",
    boxShadow: "0 10px 28px rgba(228,87,11,0.55)",
  },
};

/** At Christmas they are a festive red. */
const SANTA_BUTTON = {
  color: "#fff",
  background: "linear-gradient(180deg, " + FROST.red + " 0%, " + FROST.redDeep + " 100%)",
  boxShadow: "0 8px 22px rgba(163,24,44,0.45)",
  "&:hover": {
    background: "linear-gradient(180deg, #F04A57 0%, #8E1426 100%)",
    boxShadow: "0 10px 28px rgba(163,24,44,0.55)",
  },
};

/** Either season turns the hero into a night sky. */
const SEASONAL = HALLOWEEN || CHRISTMAS;
const CTA = HALLOWEEN ? PUMPKIN_BUTTON : CHRISTMAS ? SANTA_BUTTON : undefined;

/**
 * The hero's content above the night backdrop — and see-through to the pointer
 * wherever it has no content of its own, so the moon behind it stays clickable.
 */
const ABOVE_BACKDROP = {
  position: "relative",
  zIndex: 1,
  pointerEvents: "none",
  "& > *": { pointerEvents: "auto" },
};

const STAT_SX = SEASONAL ? { color: "#fff" } : undefined;
const STAT_CAPTION_SX = SEASONAL ? { color: "rgba(255,255,255,0.62)" } : undefined;

const BENEFITS = [
  {
    icon: <VerifiedOutlined />,
    title: "Тільки перевірені квартири",
    text: "Кожен об'єкт додає рієлтор після особистого огляду. Жодних фейкових оголошень і чужих фото.",
  },
  {
    icon: <BoltOutlined />,
    title: "База оновлюється щодня",
    text: "Щойно квартиру здають — вона зникає з каталогу. Ви не витрачаєте час на дзвінки в нікуди.",
  },
  {
    icon: <SupportAgentOutlined />,
    title: "Рієлтор поруч на кожному етапі",
    text: "Домовляємось про перегляд, супроводжуємо на зустрічі та перевіряємо договір оренди.",
  },
];

function HeroSearch() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState("");
  const [region, setRegion] = useState("");
  const [priceTo, setPriceTo] = useState("");

  const submit = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (rooms) params.set("rooms", rooms);
    if (region) params.set("region", region);
    if (priceTo) params.set("priceTo", priceTo);
    navigate("/katalog" + (params.toString() ? "?" + params.toString() : ""));
  };

  return (
    <Paper
      component="form"
      onSubmit={submit}
      elevation={0}
      sx={{
        p: { xs: 2, md: 2 },
        borderRadius: radius.xl,
        boxShadow: HALLOWEEN
          ? "0 22px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,122,26,0.28)"
          : CHRISTMAS
          ? "0 22px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,207,90,0.35)"
          : shadow.lg,
        // For the Christmas garland draped over the top edge.
        position: "relative",
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: { xs: "1fr", md: "1fr 1.3fr 1fr auto" },
        alignItems: "center",
      }}
    >
      {CHRISTMAS && <GarlandDrape />}
      <TextField select size="small" label="Кімнат" value={rooms} onChange={(e) => setRooms(e.target.value)}>
        <MenuItem value="">Будь-яка</MenuItem>
        <MenuItem value="1">1</MenuItem>
        <MenuItem value="2">2</MenuItem>
        <MenuItem value="3">3</MenuItem>
        <MenuItem value="4+">4 і більше</MenuItem>
      </TextField>

      <TextField select size="small" label="Район" value={region} onChange={(e) => setRegion(e.target.value)}>
        <MenuItem value="">Весь Львів</MenuItem>
        {LVIV_DISTRICTS.map((district) => (
          <MenuItem key={district} value={district}>
            {district}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        size="small"
        type="number"
        label="Ціна до, грн"
        value={priceTo}
        onChange={(e) => setPriceTo(e.target.value)}
      />

      <Button
        type="submit"
        variant="contained"
        size="large"
        startIcon={<SearchOutlined />}
        sx={CTA}
      >
        Знайти
      </Button>
    </Paper>
  );
}

export default function HomePage() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    loadListings()
      .then((result) => {
        if (alive) setItems(result);
      })
      .catch((err) => {
        console.error(err);
        if (alive) setError(err);
      });
    return () => {
      alive = false;
    };
  }, []);

  const featured = useMemo(() => (items || []).slice(0, 6), [items]);

  const stats = useMemo(() => {
    if (!items || items.length === 0) return null;

    // Prices are a mix of UAH and USD, so the cheapest listing has to be found
    // on the hryvnia equivalent — then shown in its own currency.
    let cheapest = null;
    let cheapestInBase = null;
    items.forEach((item) => {
      const price = itemPriceInBase(item);
      if (price === null) return;
      if (cheapestInBase === null || price < cheapestInBase) {
        cheapestInBase = price;
        cheapest = item;
      }
    });

    const districts = {};
    items.forEach((i) => {
      if (i.region) districts[i.region] = true;
    });

    return {
      total: items.length,
      cheapest: cheapest,
      districts: Object.keys(districts).length,
    };
  }, [items]);

  return (
    <Box>
      {/* ---------------------------------------------------------------- Hero */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: 6, md: 10 },
          // Room at the bottom for the rooftops and the pumpkin patch, or the
          // Christmas street.
          pb: HALLOWEEN ? { xs: 11, md: 16 } : CHRISTMAS ? { xs: 15, md: 21 } : { xs: 8, md: 12 },
          overflow: "hidden",
          background: HALLOWEEN
            ? NIGHT_SKY
            : CHRISTMAS
            ? FROST.sky
            : "radial-gradient(1100px 520px at 12% -10%, " +
              tokens.blue100 +
              " 0%, rgba(221,235,253,0) 60%), " +
              "radial-gradient(760px 420px at 92% 0%, " +
              tokens.sand100 +
              " 0%, rgba(251,239,216,0) 62%), " +
              tokens.surface,
        }}
      >
        {HALLOWEEN && <HalloweenHero />}
        {CHRISTMAS && <ChristmasHero />}
        <Container maxWidth="lg" sx={SEASONAL ? ABOVE_BACKDROP : undefined}>
          <Stack spacing={3} sx={{ maxWidth: 780 }}>
            <Chip
              label="Актуальна база оренди у Львові"
              icon={
                HALLOWEEN ? (
                  <Pumpkin style={{ width: 18, height: 17 }} />
                ) : CHRISTMAS ? (
                  <Sprite atlas={ICONS} name="gift" scale={0.17} />
                ) : undefined
              }
              sx={
                HALLOWEEN
                  ? {
                      alignSelf: "flex-start",
                      backgroundColor: "rgba(255,122,26,0.14)",
                      border: "1px solid rgba(255,122,26,0.38)",
                      color: NIGHT.ember,
                      fontWeight: 700,
                      pl: 0.5,
                    }
                  : CHRISTMAS
                  ? {
                      alignSelf: "flex-start",
                      backgroundColor: "rgba(255,207,90,0.14)",
                      border: "1px solid rgba(255,207,90,0.4)",
                      color: "#FFD978",
                      fontWeight: 700,
                      pl: 0.5,
                    }
                  : {
                      alignSelf: "flex-start",
                      backgroundColor: tokens.blue50,
                      color: tokens.blue700,
                      fontWeight: 700,
                    }
              }
            />
            <Typography
              variant="h1"
              sx={
                SEASONAL
                  ? { color: "#fff", textShadow: "0 2px 18px rgba(0,0,0,0.55)" }
                  : undefined
              }
            >
              Знайдіть квартиру у Львові,{" "}
              <Box
                component="span"
                sx={
                  HALLOWEEN
                    ? {
                        color: NIGHT.pumpkin,
                        fontFamily: SPOOKY_FONT,
                        // One weight only — a faked bold smears the drips.
                        fontWeight: 400,
                        letterSpacing: "0.01em",
                        textShadow: "0 0 22px rgba(255,122,26,0.55)",
                      }
                    : CHRISTMAS
                    ? {
                        color: FROST.gold,
                        fontFamily: PIXEL_FONT,
                        fontWeight: 400,
                        fontSize: "0.7em",
                        lineHeight: 1.7,
                        // A hard red drop shadow, the pixel-art way, plus a glow.
                        textShadow: "0 3px 0 " + FROST.redDeep + ", 0 0 24px rgba(255,207,90,0.45)",
                      }
                    : { color: tokens.blue600 }
                }
              >
                поки її не здали
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: SEASONAL ? "rgba(255,255,255,0.78)" : tokens.ink500,
                maxWidth: 620,
                fontSize: "1.1rem",
              }}
            >
              {SITE.description}
            </Typography>
          </Stack>

          <Box sx={{ mt: 5, maxWidth: 940 }}>
            <HeroSearch />
          </Box>

          {/*
            The number sits on its own line above the label, so each label is
            written as a standalone caption in the genitive plural. Agreeing it
            with the count via plural() is correct in running text but reads
            wrong detached — "1191" above "квартира у базі".
          */}
          {stats && (
            <Stack direction="row" spacing={{ xs: 3, md: 6 }} sx={{ mt: 5 }} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="h3" sx={STAT_SX}>{formatNumber(stats.total)}</Typography>
                <Typography variant="caption" sx={STAT_CAPTION_SX}>квартир у базі</Typography>
              </Box>
              <Box>
                <Typography variant="h3" sx={STAT_SX}>{stats.districts}</Typography>
                <Typography variant="caption" sx={STAT_CAPTION_SX}>районів міста</Typography>
              </Box>
              {stats.cheapest && (
                <Box>
                  <Typography variant="h3" sx={STAT_SX}>
                    від {formatPriceShort(stats.cheapest.cost, stats.cheapest.currency)}
                  </Typography>
                  <Typography variant="caption" sx={STAT_CAPTION_SX}>за місяць</Typography>
                </Box>
              )}
            </Stack>
          )}
        </Container>
      </Box>

      {/* ----------------------------------------------------------- Listings */}
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: "relative" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: radius.xl,
            boxShadow: shadow.md,
            position: "relative",
          }}
        >
          {HALLOWEEN && (
            <Cobweb
              color="rgba(14,27,46,0.13)"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 130,
                height: 130,
                transform: "scaleX(-1)",
                pointerEvents: "none",
              }}
            />
          )}
          {CHRISTMAS && (
            // A pine sprig sitting on the panel's top edge, clear of the header
            // row, with an ornament hanging off it.
            <Box
              aria-hidden="true"
              sx={{ position: "absolute", top: -44, right: { xs: 12, md: 210 }, pointerEvents: "none" }}
            >
              <Sprite atlas={ICONS} name="pineBranch" scale={0.72} style={{ transform: "rotate(-6deg)" }} />
              <Box
                className="xm-swing"
                sx={{ position: "absolute", top: 50, left: 34, display: { xs: "none", md: "block" } }}
              >
                <Sprite atlas={ICONS} name="ornamentRed" scale={0.36} />
              </Box>
            </Box>
          )}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "flex-end" }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="overline" sx={{ color: tokens.blue600 }}>
                Свіжі пропозиції
              </Typography>
              <Typography variant="h2">Нещодавно додані квартири</Typography>
            </Box>
            <Button component={RouterLink} to="/katalog" endIcon={<ArrowForward />}>
              Весь каталог
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ borderRadius: radius.md }}>
              Не вдалося завантажити базу квартир. Оновіть сторінку або спробуйте
              трохи пізніше.
            </Alert>
          )}

          <Grid container spacing={2.5}>
            {items === null &&
              !error &&
              [0, 1, 2, 3, 4, 5].map((n) => (
                <Grid item xs={12} sm={6} md={4} key={n}>
                  <Skeleton variant="rounded" height={330} sx={{ borderRadius: radius.lg }} />
                </Grid>
              ))}

            {featured.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <ListingCard item={item} />
              </Grid>
            ))}
          </Grid>

          {items !== null && items.length === 0 && !error && (
            <Typography sx={{ py: 6, textAlign: "center", color: tokens.ink500 }}>
              Наразі в базі немає доступних квартир. Зателефонуйте нам — підберемо
              варіант вручну.
            </Typography>
          )}
        </Paper>
      </Container>

      {/* ----------------------------------------------------------- Benefits */}
      <Container maxWidth="lg" sx={{ mt: { xs: 7, md: 10 } }}>
        <Grid container spacing={3}>
          {BENEFITS.map((benefit) => (
            <Grid item xs={12} md={4} key={benefit.title}>
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: radius.md,
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: HALLOWEEN ? NIGHT.dusk : CHRISTMAS ? FROST.sky : tokens.blue50,
                    color: HALLOWEEN ? NIGHT.ember : CHRISTMAS ? FROST.gold : tokens.blue600,
                    boxShadow: HALLOWEEN
                      ? "0 6px 18px rgba(255,122,26,0.22)"
                      : CHRISTMAS
                      ? "0 6px 18px rgba(2,39,94,0.3)"
                      : "none",
                  }}
                >
                  {benefit.icon}
                </Box>
                <Typography variant="h5">{benefit.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {benefit.text}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* --------------------------------------------------------- Quiz promo */}
      <Container maxWidth="lg" sx={{ mt: { xs: 7, md: 10 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: radius.xl,
            background: HALLOWEEN
              ? "linear-gradient(120deg, " + NIGHT.sky + " 0%, " + NIGHT.dusk + " 100%)"
              : CHRISTMAS
              ? "linear-gradient(120deg, " + FROST.sky + " 0%, " + FROST.pine + " 100%)"
              : "linear-gradient(120deg, " + tokens.ink + " 0%, " + tokens.blue900 + " 100%)",
            color: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              right: -60,
              top: -60,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background: HALLOWEEN
                ? "radial-gradient(circle, rgba(255,122,26,0.45), transparent 65%)"
                : CHRISTMAS
                ? "radial-gradient(circle, rgba(255,207,90,0.38), transparent 65%)"
                : "radial-gradient(circle, rgba(232,169,61,0.35), transparent 65%)",
            }}
          />
          {HALLOWEEN && (
            // Black bats against the orange glow in the corner.
            <Box
              aria-hidden="true"
              sx={{
                position: "absolute",
                top: { xs: 10, md: 18 },
                right: { xs: 12, md: 36 },
                width: 120,
                height: 70,
                pointerEvents: "none",
              }}
            >
              <div className="hw-bob" style={{ position: "absolute", left: 0, top: 18, width: 42 }}>
                <Bat flapSpeed={0.22} />
              </div>
              <div
                className="hw-bob"
                style={{ position: "absolute", left: 58, top: 0, width: 30, animationDelay: "-1s" }}
              >
                <Bat flapSpeed={0.18} />
              </div>
              <div
                className="hw-bob"
                style={{ position: "absolute", left: 96, top: 30, width: 22, animationDelay: "-1.8s" }}
              >
                <Bat flapSpeed={0.2} />
              </div>
            </Box>
          )}
          {CHRISTMAS && (
            // Bells hung from the top edge, swinging in the warm corner glow.
            <Box
              aria-hidden="true"
              className="xm-swing"
              sx={{ position: "absolute", top: -4, right: { xs: 14, md: 44 }, pointerEvents: "none" }}
            >
              <Sprite atlas={GARLAND} name="bells" />
            </Box>
          )}
          <Grid container spacing={3} alignItems="center" sx={{ position: "relative" }}>
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <ExtensionOutlined sx={{ color: tokens.sand500 }} />
                <Typography variant="overline" sx={{ color: tokens.sand500 }}>
                  Щодня новий
                </Typography>
              </Stack>
              <Typography variant="h2" sx={{ color: "#fff", mb: 1.5 }}>
                Кросворд дня від LeoCity
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.78)", maxWidth: 560 }}>
                Невеликий український кросворд про Львів, житло та побут.
                Нова сітка щоранку — перевірте, чи станете сьогодні першим,
                хто його розгадає.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { md: "right" } }}>
              <Button
                component={RouterLink}
                to="/krosvord"
                size="large"
                variant="contained"
                color="secondary"
                endIcon={<ArrowForward />}
                sx={CTA}
              >
                Розгадати кросворд
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}
